import { useMemo } from 'react';
import { WorkItem } from '../../workitems/workItemTypes';
import { LeaveEntry, PublicHoliday } from '@techgitter/contracts';
import { ConflictWarning } from '../calendarTypes';
import { getAllConflicts } from '../utils/conflictUtils';

/**
 * Computes conflict warnings for all appointments based on
 * double-bookings, leave overlaps, and public holidays.
 *
 * Returns a Map keyed by appointment guid for O(1) lookup.
 */
export function useConflictDetection(
  appointments: WorkItem[],
  leaveEntries: LeaveEntry[],
  holidays: PublicHoliday[],
) {
  const conflictMap = useMemo(() => {
    const map = new Map<string, ConflictWarning[]>();

    for (const appt of appointments) {
      const warnings = getAllConflicts(appt, appointments, leaveEntries, holidays);
      if (warnings.length > 0) {
        map.set(appt.guid, warnings);
      }
    }

    return map;
  }, [appointments, leaveEntries, holidays]);

  const getConflicts = (appointmentGuid: string): ConflictWarning[] => {
    return conflictMap.get(appointmentGuid) ?? [];
  };

  const hasConflicts = (appointmentGuid: string): boolean => {
    return conflictMap.has(appointmentGuid);
  };

  return { conflictMap, getConflicts, hasConflicts };
}
